import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMethod, useUser } from '/imports/hooks';
import { consumptionPullHasProblems, getConsumptionIntervals, pullConsumptionIntervals } from '/imports/api/consumptionIntervals';
import { MILLIS_24_HOURS } from '/imports/constants';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartColors, formatDate, formatDateTime, formatNumber } from '/imports/utils';
import { ConsumptionMeter } from '/imports/api/consumptionMeters';
import { strings } from '/imports/strings';
import { DateTimeInput } from '/imports/ui/DateTimeInput';
import { Button } from '/imports/ui/Button';
import { toastOnError } from '/imports/ui/utils';

type ConsumptionIntervalsVsTimeChartProps = {
  meter: ConsumptionMeter;
};

const ConsumptionIntervalsVsTimeChartStyled = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 20rem;
  .wrapper {
    height: 100%;
    min-height: 20rem;
    overflow: hidden;
  }
  .stats {
    display: flex;
    gap: 2rem;
    justify-content: center;
  }
  .range-picker {
    position: absolute;
    top: -2.5rem;
    right: 1rem;
    display: flex;
    gap: 0.5rem;
    span {
      margin-right: 0.5rem;
    }
    .clear {
      right: 0.75rem;
    }
  }
  .overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0.5rem;
    left: 0;
    background-color: white;
    z-index: 111;
  }
`;

/** Bar chart of a meter's consumption intervals over a selectable time range, with super-user pull. */
export const ConsumptionIntervalsVsTimeChart = (props: ConsumptionIntervalsVsTimeChartProps) => {
  const { meter } = props;
  const meterId = meter._id;
  const zone = meter.timezone;
  const user = useUser();
  const format = user?.dateTimeFormat;
  const [initializing, setInitializing] = useState(true);
  const [from, setFrom] = useState<Date>(new Date(Date.now() - 7 * MILLIS_24_HOURS));
  const [to, setTo] = useState<Date>(new Date());
  const [reload, setReload] = useState(Math.random());
  const [series, intervalsLoading] = useMethod(getConsumptionIntervals, { meterId, from, to }, [reload]);
  const rows = series?.intervals;
  const totalConsumption = useMemo(() => (rows || []).reduce((t, i) => t + i.consumption, 0), [rows]);
  useEffect(() => { if (initializing && !intervalsLoading) setInitializing(false); }, [intervalsLoading]);
  /** Triggers a synchronous interval pull, polling for refresh while it runs. */
  const pullConsumption = toastOnError(async () => {
    const interval = setInterval(() => setReload(Math.random()), 2500);
    const toastId = toast.info(strings.Running(), { autoClose: MILLIS_24_HOURS });
    const result = await pullConsumptionIntervals.call({ meterIds: [meterId], from, to, sync: true });
    clearInterval(interval);
    setReload(Math.random());
    toast.dismiss(toastId);
    // The chart used to dismiss its "running" toast and say nothing either way, so a pull that downloaded a
    // file and failed to read a row of it looked exactly like one that found no data.
    if (consumptionPullHasProblems(result)) toast.warn(strings.ConsumptionPullProblems(result.skipped + result.failed, result.filesFailed));
    else toast.success(strings.ConsumptionPullComplete(result?.filesProcessed || 0));
  });
  // The server picks the bucket width from the range, so a year draws twelve bars instead of 35,000. The
  // label says which, because a daily bar and a 15-minute bar look the same on an unlabelled axis.
  const granularity = series?.granularity;
  const resolutionLabel = granularity === 'monthly' ? strings.ChartResolutionMonthly()
    : granularity === 'daily' ? strings.ChartResolutionDaily()
      : granularity === 'hourly' ? strings.ChartResolutionHourly() : strings.ChartResolutionFifteenMin();
  const formatBucket = (timestamp: number) => (granularity === 'monthly' || granularity === 'daily'
    ? formatDate(new Date(timestamp), format, zone)
    : formatDateTime(new Date(timestamp), format, zone));
  return (
    <ConsumptionIntervalsVsTimeChartStyled className="consumption-intervals-vs-time-chart">
      {
        initializing ?
          <LoadingFill />
          :
          <div className="wrapper" >
            <ResponsiveContainer width="100%" height="100%">
              {
                intervalsLoading ?
                  <div className="overlay"><LoadingFill /></div>
                  :
                  <BarChart
                    data={rows}
                    margin={{
                    }}
                  >
                    {
                      rows?.length ?
                        <XAxis type="number" dataKey="startTime" name="timestamp" domain={['dataMin', 'dataMax']} tickFormatter={(item) => formatBucket(item)} />
                        :
                        <XAxis type="number" dataKey="startTime" name="timestamp" domain={['dataMin', 'dataMax']} />
                    }
                    <YAxis unit="kWh" width={90} />
                    <ReferenceLine y={0} stroke="#000" />
                    <Tooltip labelFormatter={(timestamp) => formatBucket(timestamp)} formatter={(value) => `${formatNumber(parseFloat((value || '0') as string), 1)}`} />
                    <Bar dataKey="consumption" barSize={20} name={strings.Consumption()} fill={chartColors[0]} unit="kWh" isAnimationActive={false} />
                  </BarChart>
              }
            </ResponsiveContainer>
            <div className="range-picker">
              <div>
                <span>{strings.From()}</span><DateTimeInput value={from} onChange={(from) => from && setFrom(from)} zone={zone} />
              </div>
              <div>
                <span>{strings.To()}</span><DateTimeInput value={to} onChange={(to) => to && setTo(to)} zone={zone} />
              </div>
              <Button onClick={() => setReload(Math.random())}>{strings.Go()}</Button>
              {user?.isSuperUser ? <Button onClick={() => pullConsumption()}>{strings.Pull()}</Button> : null}
            </div>
            <div className="stats">
              <span>{strings.TotalConsumption()}: {formatNumber(totalConsumption)} kWh</span>
              <span>{strings.ChartResolution()}: {resolutionLabel}</span>
            </div>
          </div>
      }
    </ConsumptionIntervalsVsTimeChartStyled>
  );
};